<template>
  <div
    ref="rootEl"
    class="cal"
    :class="{ 'cal--compact': compact, 'cal--dragging': isDragging }"
    v-bind="passThroughAttrs"
  >
    <CalendarToolbar
      :title="title"
      :view="view"
      :is-today-visible="isTodayVisible"
      @prev="prev"
      @next="next"
      @today="goToToday"
      @update:view="setView"
    >
      <template
        v-if="$slots['toolbar-start']"
        #start
      >
        <slot name="toolbar-start" />
      </template>
      <template
        v-if="$slots['toolbar-end']"
        #end
      >
        <slot name="toolbar-end" />
      </template>
    </CalendarToolbar>

    <div class="cal__block">
      <BlockUI :blocked="loading">
        <div
          class="cal__body"
          v-bind="resolvePassThrough(pt, 'body')"
        >
      <CalendarMonthView
        v-if="view === 'month' && !agendaDate"
        :weeks="weeks"
        :weekday-names="weekdayNames"
        :events="displayEvents"
        :blockouts="blockouts"
        :capacity-rules="capacityRules"
        :max-events-per-cell="maxEventsPerCell"
        :compact="compact"
        :hour12="hour12"
        :timezone="timezone"
        :dragging-event-id="draggingEventId"
        :drop-target-date="dropTargetDate"
        :is-drop-valid="isDropValid"
        :is-event-draggable="isEventDraggable"
        @date-click="onDateClick"
        @event-click="onEventClick"
        @event-dblclick="onEventDblClick"
        @event-pointerdown="onEventPointerDown"
        @event-hover="onEventHover"
        @event-leave="schedulePopoverHide"
        @overflow-click="onOverflowClick"
      >
        <template
          v-if="$slots.event"
          #event="{ event }"
        >
          <slot
            name="event"
            :event="event"
          />
        </template>
        <template
          v-if="$slots.blockout"
          #blockout="{ blockout }"
        >
          <slot
            name="blockout"
            :blockout="blockout"
          />
        </template>
        <template
          v-if="$slots['day-cell-header']"
          #day-cell-header="slotProps"
        >
          <slot
            name="day-cell-header"
            v-bind="slotProps"
          />
        </template>
        <template
          v-if="$slots['more-link']"
          #more-link="{ count }"
        >
          <slot
            name="more-link"
            :count="count"
          />
        </template>
      </CalendarMonthView>

      <CalendarTimeGridView
        v-else-if="view !== 'month' && !agendaDate"
        ref="timeGridRef"
        :days="days"
        :events="displayEvents"
        :blockouts="blockouts"
        :time-slots="timeSlots"
        :grid-start-minute="gridStartMinute"
        :grid-end-minute="gridEndMinute"
        :slot-height="slotHeight"
        :now-indicator="nowIndicator"
        :now="now"
        :hour12="hour12"
        :timezone="timezone"
        :dragging-event-id="draggingEventId"
        :drop-target-date="dropTargetDate"
        :drop-target-all-day="dropTargetAllDay"
        :is-drop-valid="isDropValid"
        :is-event-draggable="isEventDraggable"
        @date-click="onDateClick"
        @day-header-click="onDayHeaderClick"
        @event-click="onEventClick"
        @event-dblclick="onEventDblClick"
        @event-pointerdown="onEventPointerDown"
        @event-hover="onEventHover"
        @event-leave="schedulePopoverHide"
      >
        <template
          v-if="$slots.event"
          #event="{ event }"
        >
          <slot
            name="event"
            :event="event"
          />
        </template>
        <template
          v-if="$slots.blockout"
          #blockout="{ blockout }"
        >
          <slot
            name="blockout"
            :blockout="blockout"
          />
        </template>
      </CalendarTimeGridView>

      <CalendarDayAgenda
        v-if="agendaDate"
        :date="agendaDate"
        :events="agendaEvents"
        :hour12="hour12"
        :timezone="timezone"
        @close="agendaDate = null"
        @event-click="onEventClick"
        @event-dblclick="onEventDblClick"
      >
        <template
          v-if="$slots.empty"
          #empty
        >
          <slot name="empty" />
        </template>
      </CalendarDayAgenda>

      <div
        v-if="events.length === 0 && $slots.empty && !agendaDate && !loading"
        class="cal__empty"
      >
        <slot name="empty" />
      </div>
        </div>
      </BlockUI>
    </div>

    <CalendarEventPopover
      :event="hoveredEvent"
      :anchor="hoveredAnchor"
      :avoid="overflowEl"
      :placement-mode="view === 'month' ? 'side' : 'block'"
      :trigger="eventPopoverTrigger"
      :hour12="hour12"
      :timezone="timezone"
      @keep-open="keepPopoverOpen"
      @request-close="schedulePopoverHide"
      @dismiss="hidePopover"
    >
      <template
        v-if="$slots['event-popover']"
        #content="{ event }"
      >
        <slot
          name="event-popover"
          :event="event"
        />
      </template>
    </CalendarEventPopover>

    <Teleport to="body">
      <Transition name="cal-overflow">
        <div
          v-if="overflowEvents.length > 0"
          ref="overflowEl"
          v-click-away="onOverflowClickAway"
          class="cal-overflow"
          :style="overflowStyles"
        >
          <div class="cal-overflow__card">
            <div class="cal-overflow__header">
              <div class="cal-overflow__title">{{ overflowTitle }}</div>
              <button
                type="button"
                class="cal-overflow__close"
                :aria-label="$t('ui.actions.close')"
                @click.stop="hideOverflow"
              >
                <Icon type="xmark" />
              </button>
            </div>
            <div class="cal-overflow__list">
              <CalendarEvent
                v-for="event in overflowEvents"
                :key="event.id"
                :event="event"
                variant="dot"
                :hour12="hour12"
                :timezone="timezone"
                @pointerenter="onOverflowEventHover($event, event)"
                @pointerleave="schedulePopoverHide"
                @click.stop="onEventClick(event, $event)"
                @dblclick.stop="onEventDblClick(event, $event)"
              >
                <template
                  v-if="$slots.event"
                  #default
                >
                  <slot
                    name="event"
                    :event="event"
                  />
                </template>
              </CalendarEvent>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="dragState?.active"
        class="cal-ghost"
        :style="ghostStyle"
      >
        <CalendarEvent
          :event="dragState.event"
          :variant="view === 'month' || dragState.event.allDay ? 'dot' : 'block'"
          :hour12="hour12"
          :timezone="timezone"
          is-dragging
        />
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { autoUpdate, flip, offset, shift, size, useFloating } from '@floating-ui/vue'
import { useElementSize } from '@vueuse/core'
import dayjs, { type Dayjs } from 'dayjs'
import { computed, onMounted, onUnmounted, provide, ref, toRef, watch } from 'vue'

// components
import BlockUI from '@/components/block-ui/BlockUI.vue'
import CalendarDayAgenda from '@/components/calendar/CalendarDayAgenda.vue'
import CalendarEvent from '@/components/calendar/CalendarEvent.vue'
import CalendarEventPopover from '@/components/calendar/CalendarEventPopover.vue'
import CalendarMonthView from '@/components/calendar/CalendarMonthView.vue'
import CalendarTimeGridView from '@/components/calendar/CalendarTimeGridView.vue'
import CalendarToolbar from '@/components/calendar/CalendarToolbar.vue'
import Icon from '@/components/icon/Icon.vue'

// composables
import { useCalendar } from '@/composables/useCalendar'
import { useCalendarDragDrop, type CalendarDropTarget } from '@/composables/useCalendarDragDrop'

// types
import type {
  CalendarBlockout,
  CalendarCapacityRule,
  CalendarDateClickPayload,
  CalendarDayCell,
  CalendarDropPayload,
  CalendarDropValidation,
  CalendarEvent as CalendarEventType,
  CalendarEventOpenTrigger,
  CalendarEventPopoverTrigger,
  CalendarMoreClickPayload,
  CalendarPassThrough,
  CalendarRangeChangePayload,
  CalendarViewType,
  CalendarWeekStart
} from '@/types/components/calendar'

// utilities
import { validateDrop } from '@/utilities/calendar/constraints'
import { getEventEnd, getEventStart, getEventsForDay, moveCalendarEvent, toCalendarDate } from '@/utilities/calendar/datetime'
import { CALENDAR_PASS_THROUGH_KEY, resolvePassThrough } from '@/utilities/calendar/passthrough'

/** Width at which month view switches to dots-and-count */
const COMPACT_BREAKPOINT = 640

/** Delay before the hover card appears on a chip */
const POPOVER_SHOW_DELAY_MS = 300

/** Delay when moving between chips while the card is already open */
const POPOVER_SWITCH_DELAY_MS = 50

/** Delay before hide so the pointer can reach the card */
const POPOVER_HIDE_DELAY_MS = 180

const props = withDefaults(
  defineProps<{
    /** Events to render */
    events?: CalendarEventType[]
    /** Unavailable periods painted behind events */
    blockouts?: CalendarBlockout[]
    /** Caps on how many events a window may hold */
    capacityRules?: CalendarCapacityRule[]
    /** Active layout */
    view?: CalendarViewType
    /** Date anchoring the visible period, 'YYYY-MM-DD' or ISO */
    date?: string
    /** Whether events may be dragged */
    editable?: boolean
    /** 0 = Sunday through 6 = Saturday */
    firstDay?: CalendarWeekStart
    /** Spacing between time-grid lines, in minutes */
    slotMinutes?: number
    /** Rounding applied to time-grid drops, in minutes */
    snapMinutes?: number
    /** First hour shown in the time grid */
    dayStartHour?: number
    /** Last hour shown in the time grid */
    dayEndHour?: number
    /** Chips shown per month cell before overflow; 0 fits as many as the cell can hold */
    maxEventsPerCell?: number
    /** Pixel height of one hour in the time grid */
    slotHeight?: number
    /** IANA timezone used when an event does not specify one */
    timezone?: string
    /** Use 12-hour clock labels */
    hour12?: boolean
    /** Show the current-time line in week and day views */
    nowIndicator?: boolean
    /** Covers the grid with a spinner while host data is loading */
    loading?: boolean
    /** Whether the event detail card opens on click (default) or hover */
    eventPopoverTrigger?: CalendarEventPopoverTrigger
    /** Which gesture emits `event-click` so the host can open the record */
    eventOpenTrigger?: CalendarEventOpenTrigger
    /** Styling passthrough for named sections */
    pt?: CalendarPassThrough
  }>(),
  {
    events: () => [],
    blockouts: () => [],
    capacityRules: () => [],
    view: 'month',
    date: undefined,
    editable: false,
    firstDay: 1,
    slotMinutes: 60,
    snapMinutes: 15,
    dayStartHour: 0,
    dayEndHour: 24,
    maxEventsPerCell: 0,
    slotHeight: 48,
    timezone: undefined,
    hour12: false,
    nowIndicator: true,
    loading: false,
    eventPopoverTrigger: 'click',
    eventOpenTrigger: 'dblclick',
    pt: () => ({})
  }
)

const emit = defineEmits<{
  'update:view': [view: CalendarViewType]
  'update:date': [date: string]
  'event-click': [event: CalendarEventType]
  'event-dblclick': [event: CalendarEventType]
  'event-drop': [payload: CalendarDropPayload]
  'date-click': [payload: CalendarDateClickPayload]
  'range-change': [payload: CalendarRangeChangePayload]
  'view-change': [view: CalendarViewType]
  'more-click': [payload: CalendarMoreClickPayload]
}>()

const rootEl = ref<HTMLElement | null>(null)
const timeGridRef = ref<{ getScroller: () => HTMLElement | null } | null>(null)
const overflowEl = ref<HTMLElement | null>(null)
const overflowAnchor = ref<HTMLElement | null>(null)

const { width } = useElementSize(rootEl)
const compact = computed(() => width.value > 0 && width.value < COMPACT_BREAKPOINT)

/** Live clock used by the now-indicator and the Today button */
const now = ref(dayjs())

const ptRef = toRef(props, 'pt')
provide(CALENDAR_PASS_THROUGH_KEY, ptRef)

const passThroughAttrs = computed(() => resolvePassThrough(props.pt, 'root'))

const {
  view,
  cursor,
  title,
  visibleRange,
  days,
  weeks,
  weekdayNames,
  timeSlots,
  gridStartMinute,
  gridEndMinute,
  isTodayVisible,
  next,
  prev,
  goToToday,
  goToDate,
  setView
} = useCalendar({
  initialView: props.view,
  initialDate: props.date,
  weekStart: () => props.firstDay,
  dayStartHour: () => props.dayStartHour,
  dayEndHour: () => props.dayEndHour,
  slotMinutes: () => props.slotMinutes,
  hour12: () => props.hour12,
  now
})

/** Optimistic positions keyed by event id, cleared when the parent events list changes */
const eventOverrides = ref<Map<string, CalendarEventType>>(new Map())

/** Events as currently shown, including in-progress drops */
const displayEvents = computed(() =>
  props.events.map(event => eventOverrides.value.get(event.id) ?? event)
)

/** Whether this pointer can hover (desktop); touch uses tap instead */
const canHover = ref(false)

/** Event currently described by the shared popover */
const hoveredEvent = ref<CalendarEventType | null>(null)

/** Element the shared popover is anchored to */
const hoveredAnchor = ref<HTMLElement | null>(null)

/** Overflow events shown in the "+N more" popover */
const overflowEvents = ref<CalendarEventType[]>([])

/** Date whose overflow popover is open */
const overflowDate = ref<string | null>(null)

/** Day currently listed in the mobile agenda */
const agendaDate = ref<Dayjs | null>(null)

/** Timer that delays showing the event popover */
let popoverShowTimer: ReturnType<typeof setTimeout> | null = null

/** Timer that delays hiding the event popover */
let popoverHideTimer: ReturnType<typeof setTimeout> | null = null

/** Interval that advances the now-indicator */
let nowTimer: ReturnType<typeof setInterval> | null = null

const { floatingStyles: overflowStyles } = useFloating(overflowAnchor, overflowEl, {
  placement: 'bottom-start',
  middleware: [
    offset(6),
    flip(),
    shift({ padding: 8 }),
    size({
      padding: 8,
      apply({ availableHeight, elements }) {
        // Keep the list inside the viewport so long days stay scrollable
        elements.floating.style.maxHeight = `${Math.max(0, availableHeight)}px`
      }
    })
  ],
  strategy: 'fixed',
  whileElementsMounted: autoUpdate
})

/**
 * Whether a given event may be dragged.
 * @param event - Event to test
 */
function isEventDraggable(event: CalendarEventType): boolean {
  return event.editable ?? props.editable
}

/**
 * Validates a candidate drop against blockouts, capacity, and the editable flag.
 * @param event - Event being moved
 * @param target - Location beneath the pointer
 */
function validate(event: CalendarEventType, target: CalendarDropTarget): CalendarDropValidation {
  const moved = moveCalendarEvent(event, target, props.timezone)
  const start = getEventStart(moved, props.timezone)
  const end = getEventEnd(moved, props.timezone)

  // An unresolvable target cannot accept a drop
  if (!start || !end) {
    return { allowed: false }
  }

  return validateDrop({
    event,
    start,
    end,
    events: displayEvents.value,
    blockouts: props.blockouts,
    capacityRules: props.capacityRules,
    editable: props.editable,
    timezone: props.timezone
  })
}

/**
 * Applies an optimistic move and tells the host, which may call `revert()`.
 * @param event - Event being moved
 * @param target - Accepted drop location
 */
function onDrop(event: CalendarEventType, target: CalendarDropTarget): void {
  const moved = moveCalendarEvent(event, target, props.timezone)
  const next = new Map(eventOverrides.value)

  next.set(event.id, moved)
  eventOverrides.value = next

  emit('event-drop', {
    event,
    start: moved.start,
    end: moved.end ?? null,
    allDay: Boolean(moved.allDay),
    revert: () => {
      const reverted = new Map(eventOverrides.value)

      reverted.delete(event.id)
      eventOverrides.value = reverted
    }
  })
}

const { dragState, isDragging, draggingEventId, onPointerDown } = useCalendarDragDrop({
  snapMinutes: () => props.snapMinutes,
  enabled: () => props.editable,
  canDrag: isEventDraggable,
  validate,
  onDrop,
  scrollContainer: () => timeGridRef.value?.getScroller() ?? null
})

/** Date currently highlighted as a drop target */
const dropTargetDate = computed(() => (dragState.value?.active ? (dragState.value.target?.date ?? null) : null))

/** Whether the drag is over the all-day row */
const dropTargetAllDay = computed(() => dragState.value?.target?.allDay === true)

/** Whether the highlighted target may accept the drop */
const isDropValid = computed(() => dragState.value?.validation?.allowed !== false)

/** Fixed position of the drag ghost */
const ghostStyle = computed(() => {
  const state = dragState.value

  // The ghost is not rendered unless a drag is active
  if (!state) {
    return {}
  }

  return {
    transform: `translate(${state.pointerX - state.grabOffsetX}px, ${state.pointerY - state.grabOffsetY}px)`,
    width: `${state.width}px`,
    minHeight: `${state.height}px`
  }
})

/** Events listed in the mobile agenda */
const agendaEvents = computed(() => (agendaDate.value ? getEventsForDay(displayEvents.value, agendaDate.value, props.timezone) : []))

/** Heading for the overflow popover */
const overflowTitle = computed(() => (overflowDate.value ? dayjs(overflowDate.value).format('D MMMM YYYY') : ''))

/**
 * Clears a timeout handle.
 * @param timer - Handle to clear
 */
function clearTimer(timer: ReturnType<typeof setTimeout> | null): void {
  if (timer !== null) {
    clearTimeout(timer)
  }
}

/**
 * Hides the event popover immediately.
 */
function hidePopover(): void {
  clearTimer(popoverShowTimer)
  clearTimer(popoverHideTimer)
  popoverShowTimer = null
  popoverHideTimer = null
  hoveredEvent.value = null
  hoveredAnchor.value = null
}

/**
 * Hides the overflow popover immediately.
 */
function hideOverflow(): void {
  overflowEvents.value = []
  overflowDate.value = null
  overflowAnchor.value = null
  hidePopover()
}

/**
 * Cancels a pending hide when the pointer is still on the event or popover.
 */
function keepPopoverOpen(): void {
  clearTimer(popoverHideTimer)
  popoverHideTimer = null
}

/**
 * Hides the event popover after a short delay so the pointer can reach it.
 */
function schedulePopoverHide(): void {
  // Click-mode cards stay until dismiss or a second click
  if (props.eventPopoverTrigger !== 'hover') {
    return
  }

  clearTimer(popoverShowTimer)
  popoverShowTimer = null
  clearTimer(popoverHideTimer)
  popoverHideTimer = setTimeout(() => {
    hidePopover()
  }, POPOVER_HIDE_DELAY_MS)
}

/**
 * Opens the event card after a delay, shorter when one is already visible.
 * @param event - Event under the pointer
 * @param anchor - Element to sit beside
 * @param immediate - Skip the hover delay (used for click)
 */
function openEventPopover(event: CalendarEventType, anchor: HTMLElement | null, immediate = false): void {
  keepPopoverOpen()
  clearTimer(popoverShowTimer)

  // Click should feel instant; hover waits so passing over chips is not noisy
  if (immediate) {
    hoveredEvent.value = event
    hoveredAnchor.value = anchor
    return
  }

  const delay = hoveredEvent.value ? POPOVER_SWITCH_DELAY_MS : POPOVER_SHOW_DELAY_MS

  popoverShowTimer = setTimeout(() => {
    hoveredEvent.value = event
    hoveredAnchor.value = anchor
  }, delay)
}

/**
 * Element the pointer event originated from, even when the target is a text node.
 * @param pointerEvent - Native pointer or mouse event
 */
function hoverTargetElement(pointerEvent: Event): HTMLElement | null {
  const target = pointerEvent.target

  if (target instanceof HTMLElement) {
    return target
  }

  // Text nodes do not implement closest()
  if (target instanceof Node && target.parentElement instanceof HTMLElement) {
    return target.parentElement
  }

  return pointerEvent.currentTarget instanceof HTMLElement ? pointerEvent.currentTarget : null
}

/**
 * Resolves the event chip under the pointer so the popover sits beside that item.
 * @param pointerEvent - Native pointer or mouse event
 */
function resolvePopoverAnchor(pointerEvent: Event): HTMLElement | null {
  const start = hoverTargetElement(pointerEvent)

  // Closed or detached
  if (!start) {
    return pointerEvent.currentTarget instanceof HTMLElement ? pointerEvent.currentTarget : null
  }

  const chip = start.closest('.cal-event')

  return chip instanceof HTMLElement ? chip : start
}

/**
 * Opens the shared event popover beside a chip, on hover-capable pointers only.
 * @param pointerEvent - Native pointerenter
 * @param event - Event under the pointer
 */
function onEventHover(pointerEvent: PointerEvent, event: CalendarEventType): void {
  try {
    // Click-mode ignores hover; touch would also fight a drag long-press
    if (props.eventPopoverTrigger !== 'hover' || !canHover.value || isDragging.value) {
      return
    }

    openEventPopover(event, resolvePopoverAnchor(pointerEvent))
  } catch {
    hidePopover()
  }
}

/**
 * Hover on a row inside the overflow list; still anchors to that row.
 * @param pointerEvent - Native pointerenter
 * @param event - Event under the pointer
 */
function onOverflowEventHover(pointerEvent: PointerEvent, event: CalendarEventType): void {
  try {
    if (props.eventPopoverTrigger !== 'hover' || !canHover.value || isDragging.value) {
      return
    }

    openEventPopover(event, resolvePopoverAnchor(pointerEvent))
  } catch {
    hidePopover()
  }
}

/**
 * Begins a potential drag. Overlays stay open until the drag actually starts,
 * otherwise a pointerdown would unmount the chip before its click can fire.
 * @param pointerEvent - Native pointerdown
 * @param event - Event under the pointer
 */
function onEventPointerDown(pointerEvent: PointerEvent, event: CalendarEventType): void {
  onPointerDown(pointerEvent, event)
}

/**
 * Forwards a chip click, or toggles the detail card when the popover trigger is click.
 * @param event - Event that was clicked
 * @param payload - Originating DOM event
 */
function onEventClick(event: CalendarEventType, payload: MouseEvent | KeyboardEvent): void {
  try {
    if (props.eventPopoverTrigger === 'click') {
      const anchor = payload instanceof MouseEvent ? resolvePopoverAnchor(payload) : null

      // Double-click is used to open the record, so a second click must not close the card
      if (hoveredEvent.value?.id === event.id) {
        if (props.eventOpenTrigger !== 'dblclick') {
          hidePopover()
        }

        // Still emit event-click when a single click is the open gesture
        if (props.eventOpenTrigger === 'click') {
          emit('event-click', event)
        }

        return
      }

      openEventPopover(event, anchor, true)
    }

    // Popover already consumed the click; only emit open when click is the open gesture
    if (props.eventOpenTrigger === 'click') {
      emit('event-click', event)

      // Hover cards should not linger after the host navigates
      if (props.eventPopoverTrigger === 'hover') {
        hidePopover()
        hideOverflow()
      }
    }
  } catch {
    hidePopover()
  }
}

/**
 * Forwards a chip double-click, and emits `event-click` when that is the open gesture.
 * @param event - Event that was double-clicked
 * @param _payload - Originating DOM event
 */
function onEventDblClick(event: CalendarEventType, _payload: MouseEvent): void {
  try {
    emit('event-dblclick', event)

    // Hosts such as appointments route to the record on double-click
    if (props.eventOpenTrigger === 'dblclick') {
      emit('event-click', event)
    }
  } catch {
    hidePopover()
  }
}

/**
 * Handles an empty slot or month cell click. Compact month view opens the agenda.
 * @param date - Clicked moment
 */
function onDateClick(date: Dayjs): void {
  const allDay = view.value === 'month'

  emit('date-click', { date: date.toISOString(), allDay })

  // On a phone, tapping a day lists its events instead of trying to show chips
  if (compact.value && view.value === 'month') {
    agendaDate.value = date.startOf('day')
  }
}

/**
 * Zooms from week view into the clicked day.
 * @param cell - Day header that was clicked
 */
function onDayHeaderClick(cell: CalendarDayCell): void {
  goToDate(cell.date)
  setView('day')
}

/**
 * Opens the overflow list on click, or the mobile agenda on compact layouts.
 * A second click on the same day's link closes the list.
 * @param date - Cell date as 'YYYY-MM-DD'
 * @param events - Every event in the cell
 * @param mouseEvent - Native click
 */
function onOverflowClick(date: string, events: CalendarEventType[], mouseEvent: MouseEvent): void {
  emit('more-click', { date, events })

  // Touch and compact layouts have no room for a floating list
  if (!canHover.value || compact.value) {
    const parsed = toCalendarDate(date)

    if (parsed) {
      agendaDate.value = parsed
    }

    return
  }

  // Clicking the open day's link again dismisses the list
  if (overflowDate.value === date) {
    hideOverflow()
    return
  }

  hidePopover()
  overflowDate.value = date
  overflowEvents.value = events
  overflowAnchor.value = mouseEvent.currentTarget as HTMLElement
}

/**
 * Closes the overflow list when the pointer clicks outside it.
 * Clicks on "+N more" are ignored so that link can toggle or switch days.
 * @param event - Document click from vue3-click-away
 */
function onOverflowClickAway(event: Event): void {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : []

  const isOnMoreLink = path.some(
    node => node instanceof HTMLElement && Boolean(node.closest('.cal-month__more'))
  )

  // The more link owns open/close; treating it as outside would fight the toggle
  if (isOnMoreLink) {
    return
  }

  const isOnEventPopover = path.some(node => node instanceof HTMLElement && Boolean(node.closest('.cal-popover')))

  // The hover card is teleported next to the list, so it still counts as inside the overlay
  if (isOnEventPopover) {
    return
  }

  hideOverflow()
}

watch(
  () => props.view,
  nextView => {
    // Ignore the unset default when the parent is not controlling the view
    if (nextView && nextView !== view.value) {
      setView(nextView)
    }
  }
)

watch(
  () => props.date,
  nextDate => {
    const parsed = toCalendarDate(nextDate)

    // Ignore empty or unchanged values to avoid fighting user navigation
    if (!parsed || parsed.isSame(cursor.value, 'day')) {
      return
    }

    goToDate(parsed)
  }
)

watch(view, nextView => {
  emit('update:view', nextView)
  emit('view-change', nextView)
  agendaDate.value = null
  hidePopover()
  hideOverflow()
})

watch(cursor, nextCursor => {
  emit('update:date', nextCursor.format('YYYY-MM-DD'))
  hidePopover()
  hideOverflow()
})

watch(
  visibleRange,
  range => {
    emit('range-change', { ...range, view: view.value })
  },
  { immediate: true }
)

watch(
  () => props.events,
  () => {
    eventOverrides.value = new Map()
  }
)

watch(isDragging, dragging => {
  // A drag starting over a chip would otherwise leave the popover behind
  if (dragging) {
    hidePopover()
    hideOverflow()
  }
})

onMounted(() => {
  canHover.value = window.matchMedia('(hover: hover) and (pointer: fine)').matches
  nowTimer = setInterval(() => {
    now.value = dayjs()
  }, 60_000)

  window.addEventListener('scroll', hidePopover, true)
  window.addEventListener('resize', hidePopover)
})

onUnmounted(() => {
  if (nowTimer !== null) {
    clearInterval(nowTimer)
  }

  hidePopover()
  hideOverflow()
  window.removeEventListener('scroll', hidePopover, true)
  window.removeEventListener('resize', hidePopover)
})
</script>

<style scoped>
.cal {
  --cal-event-gap: 0.25rem;
  --cal-event-padding: 0.125rem 0.25rem;
  --cal-event-radius: var(--rounded-sm);
  --cal-event-spacing: 0.0625rem;
  --cal-slot-height: 3rem;
  --cal-gutter-width: 3.5rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background-color: var(--bg-primary);
  color: var(--text-primary);
}

.cal--dragging {
  cursor: grabbing;
  user-select: none;
}

.cal__block {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

/* BlockUI is inline-block by default and would collapse the grid's percentage height */
.cal__block :deep(.block-ui-wrapper) {
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
}

.cal__body {
  position: relative;
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 0;
}

.cal__empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.cal-overflow {
  display: flex;
  z-index: 10000;
  width: max-content;
  max-width: min(18rem, calc(100vw - 1rem));
  max-height: calc(100dvh - 1rem);
}

.cal-overflow-enter-active {
  transition: opacity 0.18s ease;
}

.cal-overflow-leave-active {
  transition: opacity 0.14s ease;
}

.cal-overflow-enter-active .cal-overflow__card,
.cal-overflow-leave-active .cal-overflow__card {
  transition: transform 0.18s cubic-bezier(0.16, 1, 0.3, 1);
}

.cal-overflow-leave-active .cal-overflow__card {
  transition-duration: 0.14s;
}

.cal-overflow-enter-from,
.cal-overflow-leave-to {
  opacity: 0;
}

.cal-overflow-enter-from .cal-overflow__card,
.cal-overflow-leave-to .cal-overflow__card {
  transform: translateY(6px) scale(0.98);
}

.cal-overflow__card {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.125rem;
  min-height: 0;
  max-height: 100%;
  padding: var(--s-2);
  overflow: hidden;
  background-color: var(--bg-primary);
  border: 1px solid var(--border-secondary);
  border-radius: var(--rounded-lg);
  box-shadow: var(--shadow-lg);
  transform-origin: center top;
}

.cal-overflow__header {
  display: flex;
  flex-shrink: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--s-1);
}

.cal-overflow__title {
  padding: 0.125rem 0.25rem;
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--text-secondary);
}

.cal-overflow__close {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  margin: -0.125rem -0.125rem 0 0;
  color: var(--text-tertiary);
  font-size: 0.75rem;
  background: none;
  border: none;
  border-radius: var(--rounded-sm);
  cursor: pointer;
}

.cal-overflow__close:hover {
  color: var(--text-secondary);
  background-color: var(--bg-primary_hover);
}

.cal-overflow__list {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 0.125rem;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
}

/* Rows must keep their natural height; the list scrolls instead of compressing chips */
.cal-overflow__list :deep(.cal-event) {
  flex-shrink: 0;
}

.cal-ghost {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10001;
  pointer-events: none;
  opacity: 0.9;
  filter: drop-shadow(0 8px 16px rgb(0 0 0 / 18%));
}
</style>
